import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
} from '@blocksuite/affine-model';
import {
  type DataSourceRowsWindow,
  type DataSourceWindowRequest,
} from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  requestSystemDocLoad,
  StoreSelectionExtension,
} from '@blocksuite/store';
import {
  canonicalDatabaseRowOrder,
  databaseLineageColumns,
  databaseSearchCellText,
  normalizeDatabaseShardManifestEntry,
  projectDatabaseRowCells,
  resolveDatabaseCellStorageKey,
} from '@streamlain/database-identity';
import { encodeStateAsUpdate } from 'yjs';

import {
  type DatabaseRowShardDocLoadContext,
  type DatabaseRowShardDocLoadReason,
  type RawValueJsonConverter,
  type RowShardCells,
  type ShardedRowRecord,
} from './data-source-contracts.js';
import type {
  DatabaseShardDocument,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  getShardDirectoryWindow,
  loadedShardDirectoryRowCount,
  requireLoadedShardDirectory,
  selectShardsForRowsWindow,
} from './data-source-shard-directory.js';
import {
  retainWriteShardDocument,
  setWindowShardDocuments,
} from './data-source-shard-ownership.js';
import { onceDirectoryComplete } from './data-source-shard-writing.js';
import {
  fromJsonValue,
  isReadonlyYMap,
  normalizeDatabaseRowWindowRequest,
  normalizeRowShardCell,
  observeDatabaseSystemDocMissing,
} from './data-source-values.js';
import {
  readPendingCreatedRowCells,
  readPendingRowUpdateCells,
} from './pending-created-rows.js';
import { getShardDocRevision } from './shard-doc-revision.js';

// Remember each decoded row's shard without retaining released row caches.
// A synchronous read may patch maps before the render; preserve its pending notification.
const readPatchedProjections = new WeakSet<DatabaseSourceRuntime>();
const cachedRowShards = new WeakMap<
  ShardedRowRecord,
  {
    shard: DatabaseShardManifestEntry;
    doc: DatabaseShardDocument;
  }
>();

export function getShardDocIds(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): string[] {
  if (!shard.serverDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database row shard "${shard.shardId}" is missing its server document id.`
    );
  }
  return [shard.serverDocId];
}

export function getExistingShardDocs(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): DatabaseShardDocument[] {
  const workspace = this._model.store.workspace;
  return this.getShardDocIds(shard)
    .map(docId => workspace.getDoc(docId))
    .filter(doc => !!doc);
}

export function hasLoadedShardRows(
  this: DatabaseSourceRuntime,
  doc: DatabaseShardDocument | null | undefined
): boolean {
  return (
    !!doc &&
    doc.spaceDoc.getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY).length > 0 &&
    doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).size > 0
  );
}

export function hasCompleteLoadedShardRows(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  doc: DatabaseShardDocument | null | undefined
): boolean {
  if (!doc) return false;
  const rowOrder = doc.spaceDoc.getArray<string>(
    DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
  );
  const rowsMap = doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const rowIds = canonicalDatabaseRowOrder(
    rowOrder.toArray().map(id => String(id))
  );
  if (this.isFileIndexDatabase()) {
    return rowIds.every(rowId => rowsMap.has(rowId));
  }
  if (shard.rowCount === 0) {
    return rowOrder.length === 0 && rowsMap.size === 0;
  }
  const rows = this.readLoadedShardRows(shard);
  return (
    rowIds.length === shard.rowCount &&
    rowsMap.size >= shard.rowCount &&
    rows.length === shard.rowCount
  );
}

export function getExistingShardDoc(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): DatabaseShardDocument | null {
  const docs = this.getExistingShardDocs(shard);
  return docs.find(doc => this.hasLoadedShardRows(doc)) ?? docs[0] ?? null;
}

export function requestRowShardDocLoad(
  this: DatabaseSourceRuntime,
  docId: string,
  reason: DatabaseRowShardDocLoadReason
): void {
  if (reason === 'write') retainWriteShardDocument(this, docId);
  if (reason === 'relation') {
    this._pinnedShardedDocs.add(docId);
    this._activeShardedWindowDocs.add(docId);
  }
  if (this._rowShardDocLoadDisposers.has(docId)) {
    return;
  }

  const disposers: Array<() => void> = [
    observeDatabaseSystemDocMissing(this._hostModel.store, docId, () => {
      if (this._disposed || this._missingRowShardDocIds.has(docId)) return;
      this._missingRowShardDocIds.add(docId);
      // Observers may report synchronously while a render reads readiness.
      queueMicrotask(() => {
        if (!this._disposed) this._shardedRowsRevision.value += 1;
      });
    }),
  ];
  const context: DatabaseRowShardDocLoadContext = {
    kind: 'database-row-shard',
    blockId: this.blockId,
    databaseId: this.databaseId,
    reason,
    workspaceId: this._model.store.workspace.id,
  };
  const disposeLoad = requestSystemDocLoad(
    this._hostModel.store,
    docId,
    context
  );
  if (!disposeLoad) {
    disposers.forEach(dispose => dispose());
    return;
  }
  disposers.push(disposeLoad);
  this._rowShardDocLoadDisposers.set(docId, disposers);
}

export function observeShardDocReady(
  this: DatabaseSourceRuntime,
  doc: DatabaseShardDocument
): void {
  if (!this._shardedReadyObservedDocs.has(doc.id)) {
    this._shardedReadyObservedDocs.add(doc.id);
    const subscription = doc.slots.ready.subscribe(() =>
      this.queueShardedRowsRefresh()
    );
    this._shardedReadyDisposers.set(doc.id, () => subscription.unsubscribe());
    if (!doc.ready) {
      doc.load();
      this.queueShardedRowsRefresh();
    }
  }
}

export function ensureShardDocsLoaded(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  reason: DatabaseRowShardDocLoadReason
): DatabaseShardDocument[] {
  const docs = this.getShardDocIds(shard)
    .map(docId => {
      this.requestRowShardDocLoad(docId, reason);
      return this.ensureWorkspaceDoc(docId);
    })
    .filter(doc => !!doc);

  if (docs.length === 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Cannot load database row shard document "${shard.serverDocId}"`
    );
  }

  docs.forEach(doc => this.observeShardDocReady(doc));
  return docs;
}

export function ensureShardObserved(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  reason: DatabaseRowShardDocLoadReason = 'row-window'
): DatabaseShardDocument {
  if (reason === 'row-window')
    for (const id of this.getShardDocIds(shard))
      this._activeShardedWindowDocs.add(id);
  const docs = this.ensureShardDocsLoaded(shard, reason);
  let observerAdded = false;
  for (const shardDoc of docs) {
    const observedDocId = shardDoc.id;
    if (this._shardedObservedDocs.has(observedDocId)) {
      continue;
    }
    this._shardedObservedDocs.add(observedDocId);
    observerAdded = true;
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const rowOrder = shardDoc.spaceDoc.getArray(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const refreshRows = () => this.queueShardedRowsRefresh();
    const refreshRowOrder = () => this.queueShardedRowsRefresh();
    const refreshDocUpdate = () => this.queueShardedRowsRefresh();
    rowsMap.observeDeep(refreshRows);
    rowOrder.observeDeep(refreshRowOrder);
    shardDoc.spaceDoc.on('update', refreshDocUpdate);
    // A crewmate's row writes arrive in the shard: its pointer follows them.
    const releaseAgent = this._hostModel.store.provider
      .get(StoreSelectionExtension)
      .observeAgentDoc(shardDoc.spaceDoc);
    this._shardedDocObserverDisposers.set(observedDocId, () => {
      rowsMap.unobserveDeep(refreshRows);
      rowOrder.unobserveDeep(refreshRowOrder);
      shardDoc.spaceDoc.off('update', refreshDocUpdate);
      releaseAgent();
    });
  }
  if (observerAdded) {
    this.queueShardedRowsRefresh();
  }
  const shardDoc = this.getExistingShardDoc(shard) ?? docs[0];
  if (!shardDoc) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Cannot load database row shard document "${shard.serverDocId}"`
    );
  }
  return shardDoc;
}

/** Loads and observes the shards of a row window `owner` shows. */
export function ensureShardsForRowsWindow(
  this: DatabaseSourceRuntime,
  shards: readonly DatabaseShardManifestEntry[],
  owner = 'plain-window'
): void {
  setWindowShardDocuments(
    this,
    owner,
    new Set(shards.flatMap(shard => this.getShardDocIds(shard)))
  );
  for (const shard of shards) this.ensureShardObserved(shard);
}

export function readLoadedShardRows(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): ShardedRowRecord[] {
  const shardDoc = this.getExistingShardDoc(shard);
  if (!shardDoc) return [];
  const revision = getShardDocRevision(shardDoc.spaceDoc);
  // Rows hold every cell under its column id; a column whose type changed
  // reads its stored cells through its lineage here, the load boundary.
  const lineageColumns = databaseLineageColumns(this._model.props.columns);
  const lineage = lineageColumns.length ? JSON.stringify(lineageColumns) : '';
  const cached = this._shardedRowsByDocId.get(shardDoc.id);
  if (cached && cached.revision === revision && cached.lineage === lineage) {
    return cached.rows;
  }
  const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const rowOrder = shardDoc.spaceDoc.getArray<string>(
    DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
  );
  const rowIds = rowOrder.toArray().map(rowId => String(rowId));
  if (rowsMap.size > 0 && rowIds.length === 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database row shard "${shardDoc.id}" is missing ${DATABASE_ROW_SHARD_ORDER_ARRAY_KEY}.`
    );
  }
  const uniqueRowIds = canonicalDatabaseRowOrder(rowIds);
  const rows: ShardedRowRecord[] = [];
  for (const rowId of uniqueRowIds) {
    const rawCells = rowsMap.get(rowId);
    if (!isReadonlyYMap(rawCells)) continue;
    const cells: RowShardCells = {};
    rawCells.forEach((rawCell, columnId) => {
      cells[columnId] = normalizeRowShardCell(columnId, rawCell);
    });
    if (lineageColumns.length)
      projectDatabaseRowCells(
        lineageColumns,
        cells,
        cell => cell.value,
        (columnId, value) => ({ columnId, value })
      );
    const row = { id: rowId, cells };
    cachedRowShards.set(row, { shard, doc: shardDoc });
    rows.push(row);
  }
  this._shardedRowsByDocId.set(shardDoc.id, {
    rows,
    revision,
    lineage,
  });
  return rows;
}

export function loadShardedRowFromExistingDocs(
  this: DatabaseSourceRuntime,
  rowId: string
): void {
  // Only shards of loaded directory parts have loaded documents to read.
  const manifest = requireLoadedShardDirectory(
    this._model,
    this.databaseId
  ).loadedManifest;

  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc) continue;
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    if (!rowOrder.toArray().some(id => String(id) === rowId)) {
      continue;
    }

    let cacheChanged = false;
    for (const row of this.readLoadedShardRows(shard)) {
      this._shardedRowsById.set(row.id, row);
      cacheChanged = true;
    }
    if (cacheChanged) {
      this._shardedRowsRevision.value += 1;
    }
    return;
  }
}

export function shardedRowsEqual(
  this: DatabaseSourceRuntime,
  rowIds: string[],
  rowsById: Map<string, ShardedRowRecord>
): boolean {
  if (rowIds.length !== this._shardedRowIds.length) {
    return false;
  }
  for (let index = 0; index < rowIds.length; index++) {
    if (rowIds[index] !== this._shardedRowIds[index]) {
      return false;
    }
  }
  if (rowsById.size !== this._shardedRowsById.size) {
    return false;
  }
  for (const [rowId, row] of rowsById) {
    const previous = this._shardedRowsById.get(rowId);
    if (!previous) {
      return false;
    }
    const cellIds = Object.keys(row.cells);
    const previousCellIds = Object.keys(previous.cells);
    if (cellIds.length !== previousCellIds.length) {
      return false;
    }
    for (const cellId of cellIds) {
      const cell = row.cells[cellId];
      const previousCell = previous.cells[cellId];
      if (!cell || !previousCell) {
        return false;
      }
      if (cell.columnId !== previousCell.columnId) {
        return false;
      }
      if (!this.serializedShardedCellEquals(cell.value, previousCell.value)) {
        return false;
      }
    }
  }
  return true;
}

export function getShardedRowsWindow(
  this: DatabaseSourceRuntime,
  request: DataSourceWindowRequest
): DataSourceRowsWindow {
  this._shardedRowsRevision.value;
  const { offset, limit } = normalizeDatabaseRowWindowRequest(request);
  const window = getShardDirectoryWindow.call(this, offset, limit);
  // The directory parts of the window are on their way; its rows show once
  // they arrive. The head counts the rows meanwhile.
  if (window.status === 'loading') {
    const totalCount = loadedShardDirectoryRowCount(this);
    return {
      offset,
      limit,
      totalCount,
      hasMore: offset + limit < totalCount,
      rowIds: [],
    };
  }

  this.ensureShardsForRowsWindow(
    selectShardsForRowsWindow(window.shards, window.rowOffset, offset, limit),
    request.owner
  );

  const rowIds: string[] = [];
  const endOffset = offset + limit;
  let shardStart = window.rowOffset;
  for (const shard of window.shards) {
    const shardEnd = shardStart + shard.rowCount;
    if (shardEnd > offset && shardStart < endOffset) {
      const shardRows = this.readLoadedShardRows(shard);
      for (const row of shardRows) {
        this._shardedRowsById.set(row.id, row);
      }
      const shardRowIds = shardRows.map(row => row.id);
      const localStart = Math.max(0, offset - shardStart);
      const localEnd = Math.min(shard.rowCount, endOffset - shardStart);
      rowIds.push(...shardRowIds.slice(localStart, localEnd));
    }
    if (shardStart >= endOffset) break;
    shardStart = shardEnd;
  }

  const totalCount = window.totalCount;
  return {
    offset,
    limit,
    totalCount,
    hasMore: offset + limit < totalCount,
    rowIds,
  };
}

export function queueShardedRowsRefresh(this: DatabaseSourceRuntime): void {
  if (this._disposed) return;
  if (this._shardedRefreshFrame !== null) return;
  this._shardedRefreshFrame = requestAnimationFrame(() => {
    this._shardedRefreshFrame = null;
    this.refreshObservedShardedRowsFromDocs();
  });
}

export function getObservedManifestShards(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseShardManifestEntry[] {
  return manifest.shards.filter(shard =>
    this.getShardDocIds(shard).some(docId =>
      this._shardedObservedDocs.has(docId)
    )
  );
}

export function getActiveWindowManifestShards(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseShardManifestEntry[] {
  return manifest.shards.filter(shard =>
    this.getShardDocIds(shard).some(docId =>
      this._activeShardedWindowDocs.has(docId)
    )
  );
}

export function refreshObservedShardedRowsFromDocs(
  this: DatabaseSourceRuntime
): void {
  // Every loaded part: a row window behind a missing part refreshes too.
  const manifest = requireLoadedShardDirectory(
    this._model,
    this.databaseId
  ).loadedManifest;
  const reconciledShards =
    this.reconcileFileIndexObservedShardManifest(manifest);
  const activeWindowShards = this.getActiveWindowManifestShards(manifest);
  this.refreshShardedRowsFromDocs(
    activeWindowShards.length > 0 ? activeWindowShards : reconciledShards
  );
}

export function reconcileFileIndexObservedShardManifest(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseShardManifestEntry[] {
  const observed = this.getObservedManifestShards(manifest);
  if (!this.isFileIndexDatabase()) return observed;
  const updates: DatabaseShardManifestEntry[] = [];
  const shards = manifest.shards.map(shard => {
    if (!observed.some(candidate => candidate.shardId === shard.shardId)) {
      return shard;
    }
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) return shard;
    const rowIds = canonicalDatabaseRowOrder(
      shardDoc.spaceDoc
        .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
        .toArray()
        .map(id => String(id))
    );
    // The writer records a shard's size when it writes rows. The live shard
    // document encodes larger than the staged one the writer measured, so a
    // size-only difference is no change: republishing it would overwrite the
    // writer's size on every observation and the two would never settle.
    const unchanged = this.updateLocalShardManifestEntry(
      shard,
      rowIds,
      shard.encodedBytes
    );
    // Compare canonical entries: stored entries may come back from the
    // Rust/WASM codec with a different key order.
    if (
      JSON.stringify(normalizeDatabaseShardManifestEntry(unchanged)) ===
      JSON.stringify(normalizeDatabaseShardManifestEntry(shard))
    )
      return shard;
    const next = this.updateLocalShardManifestEntry(
      shard,
      rowIds,
      encodeStateAsUpdate(shardDoc.spaceDoc).byteLength
    );
    updates.push(next);
    return next;
  });
  if (updates.length > 0) {
    const directory = this.getCompleteShardDirectory();
    if (directory.status === 'ready') this.publishLocalShardManifest(updates);
    else if (directory.status === 'loading')
      onceDirectoryComplete(this, 'file-index-observed-manifest', manifest => {
        this.reconcileFileIndexObservedShardManifest(manifest);
      });
  }
  return shards.filter(shard =>
    this.getShardDocIds(shard).some(docId =>
      this._shardedObservedDocs.has(docId)
    )
  );
}

export function refreshShardedRowsFromDocs(
  this: DatabaseSourceRuntime,
  shards?: readonly DatabaseShardManifestEntry[]
): void {
  // Rows project from the shards of every loaded directory part.
  const manifest = requireLoadedShardDirectory(
    this._model,
    this.databaseId
  ).loadedManifest;
  if (!shards) {
    shards = this.getObservedManifestShards(manifest);
  }

  const integrityIssue = this.getAuthoritativeRowShardIntegrityIssue(manifest);
  if (integrityIssue) {
    this._rowShardIntegrityError.value = integrityIssue.message;
    return;
  }
  const pendingIntegrityIssue = this.getLoadedRowShardIntegrityIssue(manifest);
  if (pendingIntegrityIssue) {
    this._rowShardIntegrityError.value = null;
    this.scheduleLoadedRowShardIntegrityValidation(
      manifest,
      pendingIntegrityIssue
    );
    return;
  }
  if (this._rowShardIntegrityError.value !== null) {
    this._rowShardIntegrityError.value = null;
  }

  // Refresh only the requested window, then project every already
  // materialized shard cache in manifest order. Replacing the shared cache
  // with each caller's subset makes initial-window and full-projection
  // consumers oscillate forever (for example 150 -> 257 -> 150 rows).
  for (const shard of shards) {
    this.readLoadedShardRows(shard);
  }

  const rowIds: string[] = [];
  const rowsById = new Map<string, ShardedRowRecord>();
  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc) continue;
    const cached = this._shardedRowsByDocId.get(shardDoc.id);
    if (!cached) continue;
    for (const row of cached.rows) {
      const rowId = row.id;
      rowIds.push(rowId);
      rowsById.set(rowId, row);
    }
  }

  if (
    this.shardedRowsEqual(rowIds, rowsById) &&
    !readPatchedProjections.has(this)
  ) {
    return;
  }

  readPatchedProjections.delete(this);
  this._shardedRowIds = rowIds;
  this._shardedRowsById = rowsById;
  this._shardedSearchTextByRowId.clear();
  this._shardedRowsRevision.value += 1;
}

export function getShardedProjectionCellValue(
  this: DatabaseSourceRuntime,
  propertyId: string,
  serialized: unknown
): unknown {
  const propertyType = this.isSpacialProperty(propertyId)
    ? propertyId
    : this.propertyTypeGet(propertyId);
  if (!propertyType) return serialized;
  const meta = this.propertyMetaGet(propertyType);
  if (!meta) return serialized;
  const data = this.propertyDataGet(
    this.isSpacialProperty(propertyId) ? propertyType : propertyId
  );
  const normalized =
    fromJsonValue(meta as RawValueJsonConverter, {
      data,
      dataSource: this.dataSource,
      value: serialized,
    }) ?? serialized;
  return (
    meta.config.rawValue.toJson?.({
      value: normalized,
      data,
      dataSource: this.dataSource,
    }) ?? normalized
  );
}

export function getShardedRowSearchText(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyIds: readonly string[]
): string {
  const propertySignature = propertyIds.join('\u0000');
  if (propertySignature !== this._shardedSearchPropertySignature) {
    this._shardedSearchPropertySignature = propertySignature;
    this._shardedSearchTextByRowId.clear();
  }
  const cached = this._shardedSearchTextByRowId.get(rowId);
  if (cached !== undefined) return cached;

  const row = this._shardedRowsById.get(rowId);
  const text = propertyIds
    .map(propertyId =>
      databaseSearchCellText(
        this.getShardedProjectionCellValue(
          propertyId,
          row?.cells[propertyId]?.value
        )
      )
    )
    .join('\u0000')
    .toLowerCase();
  this._shardedSearchTextByRowId.set(rowId, text);
  return text;
}

export function getShardedCellValue(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): { found: boolean; value: unknown } {
  this._shardedRowsRevision.value;
  let row = this._shardedRowsById.get(rowId);
  const source = row && cachedRowShards.get(row);
  if (source) {
    const cached = this._shardedRowsByDocId.get(source.doc.id);
    if (
      cached &&
      cached.revision !== getShardDocRevision(source.doc.spaceDoc)
    ) {
      const current = this.readLoadedShardRows(source.shard);
      for (const previous of cached.rows) {
        if (this._shardedRowsById.get(previous.id) === previous)
          this._shardedRowsById.delete(previous.id);
        this._shardedSearchTextByRowId.delete(previous.id);
      }
      for (const updated of current)
        this._shardedRowsById.set(updated.id, updated);
      readPatchedProjections.add(this);
      this.queueShardedRowsRefresh();
      row = this._shardedRowsById.get(rowId);
    }
  }
  // A row still on its way to its shard doc answers from the cells written
  // to it so far (calendar event, file index: create, write, read at once).
  const stored =
    row?.cells ?? readPendingCreatedRowCells(this.storageDoc, rowId);
  if (!stored) return { found: false, value: undefined };
  // An edit sent to the row writer reads back before its receipt lands.
  const update = readPendingRowUpdateCells(this.storageDoc, rowId);
  const cells = update ? { ...stored, ...update } : stored;
  const storageKey = resolveDatabaseCellStorageKey(
    cells,
    propertyId,
    this._model.props.columns
  );
  const cell = cells[storageKey];
  if (!cell) return { found: false, value: undefined };
  return {
    found: true,
    value: this.normalizeShardedCellValueForProperty(
      rowId,
      propertyId,
      cell.value
    ),
  };
}
